import { useEffect } from "react";
import { useGetOne, type Identifier } from "ra-core";
import { useFormContext, useWatch } from "react-hook-form";
import { AutocompleteInput } from "@/components/admin/autocomplete-input";
import { ReferenceInput } from "@/components/admin/reference-input";
import type { Deal } from "@/components/atomic-crm/types";
import { useOrgModules } from "@/modules/useOrgModules";

const dealOptionText = (choice: { name?: string | null; id?: Identifier }) =>
  choice.name?.trim() || `Project #${choice.id}`;

/** Project field — only for companies with Tickets → Link to projects. */
export const TicketProjectInput = ({
  companyId,
}: {
  companyId: Identifier | null | undefined;
}) => {
  const { hasFeature } = useOrgModules();
  if (!hasFeature("tickets", "projectLinks")) return null;
  return <TicketProjectField companyId={companyId} />;
};

const TicketProjectField = ({
  companyId,
}: {
  companyId: Identifier | null | undefined;
}) => {
  const { setValue } = useFormContext();
  const dealId = useWatch({ name: "deal_id" }) as Identifier | null | undefined;

  const { data: selectedDeal } = useGetOne<Deal>(
    "deals",
    { id: dealId ?? "" },
    { enabled: dealId != null && dealId !== "" },
  );

  useEffect(() => {
    if (!selectedDeal?.company_id) return;
    if (companyId) return;
    setValue("company_id", selectedDeal.company_id, { shouldDirty: false });
  }, [companyId, selectedDeal?.company_id, setValue]);

  useEffect(() => {
    if (dealId == null || dealId === "") return;
    if (!companyId || !selectedDeal) return;
    if (String(selectedDeal.company_id) !== String(companyId)) {
      setValue("deal_id", null);
    }
  }, [companyId, dealId, selectedDeal, setValue]);

  return (
    <ReferenceInput
      source="deal_id"
      reference="deals"
      filter={companyId ? { "company_id@eq": companyId } : undefined}
    >
      <AutocompleteInput
        label="Project"
        optionText={dealOptionText}
        helperText={false}
        filterToQuery={(searchText) => ({ q: searchText })}
        labelVariant="floating"
      />
    </ReferenceInput>
  );
};
